import * as React from "react";

import { useProminentError } from "../../Common";

import {
  MyBagUserExperience_ProminentErrorAreas,
  useMyBagPresentationModel,
  useMyBagUserExperience_asController,
} from "../providers";

export const ApplyACoupon: React.FunctionComponent = () => {
  const { presentationMyBagOrder } = useMyBagPresentationModel();
  const { handlePromoCodeApply, handlePromoCodeRemove } =
    useMyBagUserExperience_asController();

  const {
    prominentError: generalProminentError,
    prominentErrorArea,
    setProminentError,
  } = useProminentError();

  const prominentError = React.useMemo(() => {
    if (
      prominentErrorArea === MyBagUserExperience_ProminentErrorAreas.PROMO_CODE
    ) {
      const thisIsMyError = generalProminentError;
      return thisIsMyError;
    }
    return null;
  }, [generalProminentError, prominentErrorArea]);

  const [value, setValue] = React.useState(
    () => presentationMyBagOrder?.promoCodeBreakdown?.code || ""
  );

  const isThereAPromoCodeApplied = React.useMemo(() => {
    return !!presentationMyBagOrder?.promoCodeBreakdown || false;
  }, [presentationMyBagOrder]);

  const handleKeyChange: React.KeyboardEventHandler<HTMLInputElement> =
    React.useCallback(
      (event) => {
        if (event.key === "Enter") {
          handlePromoCodeApply(value);
        }
      },
      [handlePromoCodeApply, value]
    );

  const handleChange: React.ChangeEventHandler<HTMLInputElement> =
    React.useCallback(
      (event) => {
        setValue(event.target.value);
      },
      [setValue]
    );

  const handleApply: React.MouseEventHandler<HTMLButtonElement> =
    React.useCallback(
      async (event) => {
        event.preventDefault();
        if (presentationMyBagOrder?.areThereAnyGiftcardsInTheOrder) {
          const errorMessage =
            "Promo codes cannot be applied to e-gift cards. Please remove the e-gift card from your bag to apply discount.";
          setProminentError(errorMessage);
          return;
        } else {
          await handlePromoCodeApply(value);
        }
      },
      [
        handlePromoCodeApply,
        presentationMyBagOrder?.areThereAnyGiftcardsInTheOrder,
        setProminentError,
        value,
      ]
    );

  const handleRemove: React.MouseEventHandler<HTMLButtonElement> =
    React.useCallback(
      async (event) => {
        event.preventDefault();
        await handlePromoCodeRemove();
        setValue("");
      },
      [handlePromoCodeRemove]
    );

  const onMessageRef: React.LegacyRef<HTMLParagraphElement> = (element) => {
    (window.require as any)(["jquery"], function ($: any) {
      $(element).html(prominentError);
    });
  };

  return (
    <form
      className={`apply-a-coupon-form ${
        isThereAPromoCodeApplied ? "applied" : "yet-to-have-applied"
      } ${prominentError ? "error" : "not-in-an-errornous-state"}`}
    >
      <div className="input-container">
        <input
          type="text"
          value={value}
          onKeyDown={handleKeyChange}
          onChange={handleChange}
          disabled={isThereAPromoCodeApplied}
          placeholder={`Enter your promo code`}
        />
        {isThereAPromoCodeApplied ? (
          <button
            className="promo-code-remove"
            onClick={handleRemove}
          >{`remove`}</button>
        ) : (
          <button
            className="promo-code-apply"
            onClick={handleApply}
          >{`apply`}</button>
        )}
      </div>
      {prominentError ? (
        <p className="brief-prominent-error" ref={onMessageRef}>
          {prominentError}
        </p>
      ) : null}
    </form>
  );
};
